"use client";

import {
	Check,
	CircleAlert,
	Download,
	FileVideo2,
	FolderOpen,
	Loader2,
	Move,
	RefreshCcw,
	ShieldCheck,
	Trash2,
	X,
} from "lucide-react";
import {
	type ChangeEvent,
	type PointerEvent as ReactPointerEvent,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useLocale, useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/utils/ui";
import { cropVideo, loadFFmpeg } from "@/lib/ffmpeg";

type ToolPhase =
	| "empty"
	| "inspecting"
	| "ready"
	| "unsupported"
	| "processing"
	| "success"
	| "failed";
type CropPreset = "free" | "16:9" | "9:16" | "1:1" | "4:5" | "4:3";
type OutputFormat = "MP4" | "MOV" | "WebM";
type OutputQuality = "Balanced" | "High" | "Smaller file";
type DragMode = "move" | "nw" | "ne" | "sw" | "se";

interface CropRect {
	x: number;
	y: number;
	width: number;
	height: number;
}

interface SourceInfo {
	width: number;
	height: number;
	duration: number;
}

interface DragState {
	pointerId: number;
	mode: DragMode;
	startX: number;
	startY: number;
	rect: CropRect;
}

const FULL_FRAME: CropRect = { x: 0, y: 0, width: 100, height: 100 };
const MIN_CROP_PERCENT = 8;
const OUTPUT_FORMATS: readonly OutputFormat[] = ["MP4", "MOV", "WebM"];
const OUTPUT_QUALITIES: readonly OutputQuality[] = [
	"Balanced",
	"High",
	"Smaller file",
];

const PRESETS: { value: CropPreset; labelKey?: "freeform"; ratio?: number }[] =
	[
		{ value: "free", labelKey: "freeform" },
		{ value: "16:9", ratio: 16 / 9 },
		{ value: "9:16", ratio: 9 / 16 },
		{ value: "1:1", ratio: 1 },
		{ value: "4:5", ratio: 4 / 5 },
		{ value: "4:3", ratio: 4 / 3 },
	];

// Percent math stays considerably easier to scan with positional bounds.
// eslint-disable-next-line opencut/prefer-object-params
function clamp(value: number, minimum: number, maximum: number) {
	return Math.min(Math.max(value, minimum), maximum);
}

function formatBytes({ bytes, locale }: { bytes: number; locale: string }) {
	if (bytes < 1024) return `${new Intl.NumberFormat(locale).format(bytes)} B`;
	const units = ["KB", "MB", "GB"];
	let value = bytes / 1024;
	let unit = units[0];
	for (let index = 1; value >= 1024 && index < units.length; index += 1) {
		value /= 1024;
		unit = units[index];
	}
	return `${new Intl.NumberFormat(locale, {
		maximumFractionDigits: value >= 10 ? 1 : 2,
	}).format(value)} ${unit}`;
}

function formatDuration({
	seconds,
	unknownDuration,
}: {
	seconds: number;
	unknownDuration: string;
}) {
	if (!Number.isFinite(seconds)) return unknownDuration;
	const minutes = Math.floor(seconds / 60);
	const remaining = Math.floor(seconds % 60);
	return `${minutes}:${remaining.toString().padStart(2, "0")}`;
}

function isOutputFormat(value: string): value is OutputFormat {
	return OUTPUT_FORMATS.some((format) => format === value);
}

function isOutputQuality(value: string): value is OutputQuality {
	return OUTPUT_QUALITIES.some((quality) => quality === value);
}

function rectForRatio({
	sourceRatio,
	targetRatio,
}: {
	sourceRatio: number;
	targetRatio: number;
}): CropRect {
	if (targetRatio <= sourceRatio) {
		const width = (targetRatio / sourceRatio) * 100;
		return { x: (100 - width) / 2, y: 0, width, height: 100 };
	}
	const height = (sourceRatio / targetRatio) * 100;
	return { x: 0, y: (100 - height) / 2, width: 100, height };
}

function roundedRect(rect: CropRect): CropRect {
	return {
		x: Number(rect.x.toFixed(4)),
		y: Number(rect.y.toFixed(4)),
		width: Number(rect.width.toFixed(4)),
		height: Number(rect.height.toFixed(4)),
	};
}

export function CropVideo() {
	const t = useTranslations("CropVideo.tool");
	const locale = useLocale();
	const inputRef = useRef<HTMLInputElement>(null);
	const previewRef = useRef<HTMLDivElement>(null);
	const dragRef = useRef<DragState | null>(null);
	const [phase, setPhase] = useState<ToolPhase>("empty");
	const [file, setFile] = useState<File | null>(null);
	const [videoUrl, setVideoUrl] = useState<string | null>(null);
	const [source, setSource] = useState<SourceInfo | null>(null);
	const [preset, setPreset] = useState<CropPreset>("free");
	const [crop, setCrop] = useState<CropRect>(FULL_FRAME);
	const [isDragging, setIsDragging] = useState(false);
	const [dropActive, setDropActive] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [format, setFormat] = useState<OutputFormat>("MP4");
	const [quality, setQuality] = useState<OutputQuality>("Balanced");
	const [keepAudio, setKeepAudio] = useState(true);
	const [progress, setProgress] = useState(0);
	const [resultBlob, setResultBlob] = useState<Blob | null>(null);
	const [resultUrl, setResultUrl] = useState<string | null>(null);
	const [processingError, setProcessingError] = useState<string | null>(null);

	useEffect(() => {
		return () => {
			if (videoUrl) URL.revokeObjectURL(videoUrl);
			if (resultUrl) URL.revokeObjectURL(resultUrl);
		};
	}, [videoUrl, resultUrl]);

	const outputPixels = useMemo(() => {
		if (!source) return null;
		return {
			x: Math.round((crop.x / 100) * source.width),
			y: Math.round((crop.y / 100) * source.height),
			width: Math.max(1, Math.round((crop.width / 100) * source.width)),
			height: Math.max(1, Math.round((crop.height / 100) * source.height)),
		};
	}, [crop, source]);

	const clearFile = () => {
		setFile(null);
		setVideoUrl(null);
		setSource(null);
		setPhase("empty");
		setPreset("free");
		setCrop(FULL_FRAME);
		setError(null);
		setProgress(0);
		setResultBlob(null);
		setResultUrl(null);
		setProcessingError(null);
		if (inputRef.current) inputRef.current.value = "";
	};

	const startCrop = async () => {
		if (!file || !source || !outputPixels) return;

		try {
			setPhase("processing");
			setProgress(0);
			setProcessingError(null);

			// Load FFmpeg if not already loaded
			await loadFFmpeg();

			const result = await cropVideo({
				file,
				options: {
					cropX: outputPixels.x,
					cropY: outputPixels.y,
					cropWidth: outputPixels.width,
					cropHeight: outputPixels.height,
					format,
					quality,
					keepAudio,
				},
				onProgress: (p) => {
					setProgress(p.percent);
				},
			});

			setResultBlob(result);
			const url = URL.createObjectURL(result);
			setResultUrl(url);
			setPhase("success");
		} catch (err) {
			console.error("Crop failed:", err);
			setPhase("failed");
			setProcessingError(t("errors.processing"));
		}
	};

	const downloadResult = () => {
		if (!resultUrl || !file) return;

		const a = document.createElement("a");
		a.href = resultUrl;
		const originalName = file.name.replace(/\.[^/.]+$/, "");
		a.download = `${originalName}-cropped.${format.toLowerCase()}`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	const resetToEditor = () => {
		setPhase("ready");
		setProgress(0);
		if (resultUrl) {
			URL.revokeObjectURL(resultUrl);
			setResultUrl(null);
		}
		setResultBlob(null);
		setProcessingError(null);
	};

	const inspectFile = (nextFile: File | undefined) => {
		if (!nextFile) return;
		setError(null);
		if (!nextFile.type.startsWith("video/")) {
			setError(t("errors.invalidFile"));
			return;
		}

		const nextUrl = URL.createObjectURL(nextFile);
		setFile(nextFile);
		setVideoUrl(nextUrl);
		setSource(null);
		setPreset("free");
		setCrop(FULL_FRAME);
		setPhase("inspecting");
	};

	const choosePreset = (nextPreset: CropPreset) => {
		setPreset(nextPreset);
		if (!source || nextPreset === "free") return;
		const selected = PRESETS.find((item) => item.value === nextPreset);
		if (!selected?.ratio) return;
		setCrop(
			rectForRatio({
				sourceRatio: source.width / source.height,
				targetRatio: selected.ratio,
			}),
		);
	};

	const resetCrop = () => {
		setPreset("free");
		setCrop(FULL_FRAME);
	};

	const startDrag = ({
		event,
		mode,
	}: {
		event: ReactPointerEvent<HTMLElement>;
		mode: DragMode;
	}) => {
		if (!source) return;
		event.preventDefault();
		event.stopPropagation();
		const preview = event.currentTarget.closest<HTMLElement>(
			"[data-crop-preview]",
		);
		preview?.setPointerCapture(event.pointerId);
		dragRef.current = {
			pointerId: event.pointerId,
			mode,
			startX: event.clientX,
			startY: event.clientY,
			rect: crop,
		};
		setIsDragging(true);
	};
	const startMoveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
		startDrag({ event, mode: "move" });
	};
	const startResizeDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
		const mode = event.currentTarget.dataset.dragMode;
		if (mode === "nw" || mode === "ne" || mode === "sw" || mode === "se") {
			startDrag({ event, mode });
		}
	};

	const moveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
		const active = dragRef.current;
		const bounds = previewRef.current?.getBoundingClientRect();
		if (!active || !bounds || active.pointerId !== event.pointerId) return;

		const dx = ((event.clientX - active.startX) / bounds.width) * 100;
		const dy = ((event.clientY - active.startY) / bounds.height) * 100;
		const next = { ...active.rect };

		if (active.mode === "move") {
			next.x = clamp(active.rect.x + dx, 0, 100 - active.rect.width);
			next.y = clamp(active.rect.y + dy, 0, 100 - active.rect.height);
		} else if (preset === "free") {
			if (active.mode.includes("w")) {
				const right = active.rect.x + active.rect.width;
				next.x = clamp(active.rect.x + dx, 0, right - MIN_CROP_PERCENT);
				next.width = right - next.x;
			}
			if (active.mode.includes("e")) {
				next.width = clamp(
					active.rect.width + dx,
					MIN_CROP_PERCENT,
					100 - active.rect.x,
				);
			}
			if (active.mode.includes("n")) {
				const bottom = active.rect.y + active.rect.height;
				next.y = clamp(active.rect.y + dy, 0, bottom - MIN_CROP_PERCENT);
				next.height = bottom - next.y;
			}
			if (active.mode.includes("s")) {
				next.height = clamp(
					active.rect.height + dy,
					MIN_CROP_PERCENT,
					100 - active.rect.y,
				);
			}
		}
		setCrop(roundedRect(next));
	};

	const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
		if (dragRef.current?.pointerId !== event.pointerId) return;
		previewRef.current?.releasePointerCapture(event.pointerId);
		dragRef.current = null;
		setIsDragging(false);
	};

	const moveCropWithKeyboard = (event: React.KeyboardEvent<HTMLDivElement>) => {
		const step = event.shiftKey ? 5 : 1;
		const offsets: Record<string, [number, number]> = {
			ArrowLeft: [-step, 0],
			ArrowRight: [step, 0],
			ArrowUp: [0, -step],
			ArrowDown: [0, step],
		};
		const offset = offsets[event.key];
		if (!offset) return;
		event.preventDefault();
		setCrop((current) => ({
			...current,
			x: clamp(current.x + offset[0], 0, 100 - current.width),
			y: clamp(current.y + offset[1], 0, 100 - current.height),
		}));
	};

	const updatePixelField = ({
		field,
		value,
	}: {
		field: "x" | "y" | "width" | "height";
		value: number;
	}) => {
		if (!source || !Number.isFinite(value)) return;
		setPreset("free");
		setCrop((current) => {
			const next = { ...current };
			if (field === "x") {
				next.x = (clamp(value, 0, source.width - 1) / source.width) * 100;
				next.width = Math.min(next.width, 100 - next.x);
			} else if (field === "y") {
				next.y = (clamp(value, 0, source.height - 1) / source.height) * 100;
				next.height = Math.min(next.height, 100 - next.y);
			} else if (field === "width") {
				next.width =
					(clamp(
						value,
						1,
						source.width - Math.round((next.x / 100) * source.width),
					) /
						source.width) *
					100;
			} else {
				next.height =
					(clamp(
						value,
						1,
						source.height - Math.round((next.y / 100) * source.height),
					) /
						source.height) *
					100;
			}
			return roundedRect(next);
		});
	};

	const cropSize = Math.round(Math.min(crop.width, crop.height));
	const adjustLockedCropSize = (value: number) => {
		if (!source || preset === "free") return;
		const selected = PRESETS.find((item) => item.value === preset);
		if (!selected?.ratio) return;
		const base = rectForRatio({
			sourceRatio: source.width / source.height,
			targetRatio: selected.ratio,
		});
		const scale = value / Math.min(base.width, base.height);
		const width = clamp(base.width * scale, MIN_CROP_PERCENT, 100);
		const height = clamp(base.height * scale, MIN_CROP_PERCENT, 100);
		setCrop({
			x: clamp(crop.x + (crop.width - width) / 2, 0, 100 - width),
			y: clamp(crop.y + (crop.height - height) / 2, 0, 100 - height),
			width,
			height,
		});
	};

	return (
		<section
			aria-labelledby="crop-workspace-title"
			aria-label={t("ariaLabel")}
			className="mx-auto w-full max-w-[84rem]"
		>
			<div className="overflow-hidden rounded-[2rem] border border-blue-200/80 bg-slate-100/92 shadow-[0_30px_90px_-50px_rgba(37,99,235,0.55)] backdrop-blur-xl dark:border-blue-900/75 dark:bg-slate-950/90 dark:shadow-[0_30px_90px_-50px_rgba(34,211,238,0.3)]">
				<div className="flex flex-col gap-3 border-b border-blue-100 bg-white/82 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7 dark:border-blue-950 dark:bg-slate-950/80">
					<div>
						<h2 id="crop-workspace-title" className="font-bold tracking-tight">
							{t("header.title")}
						</h2>
						<p className="text-muted-foreground mt-0.5 text-sm">
							{t("header.description")}
						</p>
					</div>
					<span className="inline-flex min-h-8 w-fit items-center gap-1.5 rounded-full border border-cyan-200 bg-cyan-50 px-3 text-xs font-semibold text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/45 dark:text-cyan-100">
						<ShieldCheck className="size-3.5" aria-hidden="true" />
						{t("header.local")}
					</span>
				</div>

				<div className="grid gap-5 p-4 sm:p-6 lg:grid-cols-[minmax(0,1.25fr)_minmax(20rem,0.75fr)] lg:p-8">
					<div className="min-w-0">
						{phase === "empty" ? (
							<div
								className={cn(
									"flex min-h-[26rem] flex-col items-center justify-center rounded-3xl border-2 border-dashed bg-white/88 p-6 text-center transition-colors dark:bg-slate-900/82",
									dropActive
										? "border-cyan-400 bg-cyan-50/80 dark:bg-cyan-950/35"
										: "border-blue-200 dark:border-blue-900",
								)}
								onDragEnter={(event) => {
									event.preventDefault();
									setDropActive(true);
								}}
								onDragOver={(event) => event.preventDefault()}
								onDragLeave={() => setDropActive(false)}
								onDrop={(event) => {
									event.preventDefault();
									setDropActive(false);
									inspectFile(event.dataTransfer.files[0]);
								}}
							>
								<span className="flex size-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-400 text-white shadow-lg shadow-blue-500/20">
									<FileVideo2 className="size-7" aria-hidden="true" />
								</span>
								<p className="mt-5 text-xl font-bold">{t("drop.title")}</p>
								<Button
									className="mt-5 min-h-12 rounded-xl px-6"
									onClick={() => inputRef.current?.click()}
								>
									<FolderOpen className="size-4" aria-hidden="true" />
									{t("drop.choose")}
								</Button>
								<p className="text-muted-foreground mt-4 text-xs">
									{t("drop.privacy")}
								</p>
							</div>
						) : (
							<div className="rounded-3xl border border-blue-100 bg-white/88 p-3 shadow-sm sm:p-4 dark:border-blue-950 dark:bg-slate-900/82">
								<div
									ref={previewRef}
									data-crop-preview
									className="relative mx-auto w-full touch-none overflow-hidden rounded-2xl bg-slate-950 shadow-inner"
									style={{
										aspectRatio: source
											? `${source.width} / ${source.height}`
											: "16 / 9",
									}}
									onPointerMove={moveDrag}
									onPointerUp={endDrag}
									onPointerCancel={endDrag}
								>
									{videoUrl ? (
										<video
											src={videoUrl}
											className="absolute inset-0 size-full object-contain"
											controls={phase === "ready"}
											playsInline
											preload="metadata"
											onLoadedMetadata={(event) => {
												const video = event.currentTarget;
												if (!video.videoWidth || !video.videoHeight) {
													setPhase("unsupported");
													setError(t("errors.metadata"));
													return;
												}
												setSource({
													width: video.videoWidth,
													height: video.videoHeight,
													duration: video.duration,
												});
												setPhase("ready");
											}}
											onError={() => {
												setPhase("unsupported");
												setError(t("errors.preview"));
											}}
										>
											<track
												kind="captions"
												src="data:text/vtt,WEBVTT%0A%0A"
												srcLang={locale}
												label={t("preview.noCaptions")}
											/>
										</video>
									) : null}

									{phase === "inspecting" ? (
										<div
											className="absolute inset-0 flex items-center justify-center bg-slate-950/85 text-sm font-semibold text-white"
											role="status"
										>
											{t("reading")}
										</div>
									) : null}

									{phase === "ready" ? (
										<div
											role="slider"
											tabIndex={0}
											aria-label={t("preview.cropArea")}
											aria-valuemin={0}
											aria-valuemax={Math.round(100 - crop.width)}
											aria-valuenow={Math.round(crop.x)}
											aria-valuetext={
												outputPixels
													? t("preview.cropAreaValue", {
															width: outputPixels.width,
															height: outputPixels.height,
															x: outputPixels.x,
															y: outputPixels.y,
														})
													: undefined
											}
											className={cn(
												"absolute cursor-move border-2 border-cyan-300 outline-none ring-inset shadow-[0_0_0_9999px_rgba(2,6,23,0.62)] focus-visible:ring-4 focus-visible:ring-white/80",
												isDragging && "border-white",
											)}
											style={{
												left: `${crop.x}%`,
												top: `${crop.y}%`,
												width: `${crop.width}%`,
												height: `${crop.height}%`,
											}}
											onPointerDown={startMoveDrag}
											onKeyDown={moveCropWithKeyboard}
										>
											<span className="pointer-events-none absolute top-2 left-2 inline-flex items-center gap-1 rounded-md bg-slate-950/75 px-2 py-1 text-[11px] font-bold text-white">
												<Move className="size-3" aria-hidden="true" />
												{t("preview.keep")}
											</span>
											{preset === "free"
												? (["nw", "ne", "sw", "se"] as const).map((mode) => (
														<button
															key={mode}
															type="button"
															data-drag-mode={mode}
															aria-label={t(`preview.resizeHandles.${mode}`)}
															className={cn(
																"absolute size-5 rounded-full border-2 border-slate-950 bg-white shadow",
																mode.includes("n") ? "-top-2.5" : "-bottom-2.5",
																mode.includes("w") ? "-left-2.5" : "-right-2.5",
															)}
															onPointerDown={startResizeDrag}
														/>
													))
												: null}
										</div>
									) : null}
								</div>

								<div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
									<div className="min-w-0">
										<p className="truncate text-sm font-bold">{file?.name}</p>
										<p className="text-muted-foreground text-xs">
											{file ? formatBytes({ bytes: file.size, locale }) : null}
											{source
												? ` · ${source.width} × ${source.height} · ${formatDuration({ seconds: source.duration, unknownDuration: t("preview.unknownDuration") })}`
												: null}
										</p>
									</div>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => inputRef.current?.click()}
										>
											{t("replace")}
										</Button>
										<Button
											variant="outline"
											size="icon"
											className="size-8"
											aria-label={t("remove")}
											onClick={clearFile}
										>
											<Trash2 className="size-4" />
										</Button>
									</div>
								</div>
							</div>
						)}

						{error ? (
							<div
								className="mt-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950 dark:border-rose-900 dark:bg-rose-950/35 dark:text-rose-100"
								role="alert"
							>
								<CircleAlert
									className="mt-0.5 size-4 shrink-0"
									aria-hidden="true"
								/>
								<span>{error}</span>
							</div>
						) : null}
					</div>

					<div className="min-w-0 space-y-4">
						<div className="rounded-3xl border border-blue-100 bg-white/88 p-4 shadow-sm sm:p-5 dark:border-blue-950 dark:bg-slate-900/82">
							<div className="flex items-center justify-between gap-3">
								<p className="font-bold">{t("aspect.title")}</p>
								<Button
									variant="text"
									size="sm"
									onClick={resetCrop}
									disabled={!source}
								>
									<RefreshCcw className="size-3.5" />
									{t("aspect.reset")}
								</Button>
							</div>
							<div className="mt-3 grid grid-cols-3 gap-2">
								{PRESETS.map((item) => (
									<button
										key={item.value}
										type="button"
										aria-pressed={preset === item.value}
										disabled={!source}
										onClick={() => choosePreset(item.value)}
										className={cn(
											"min-h-11 rounded-xl border px-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-45",
											preset === item.value
												? "border-blue-500 bg-blue-50 text-blue-950 dark:border-cyan-500 dark:bg-cyan-950/45 dark:text-cyan-50"
												: "border-slate-200 bg-slate-50 text-slate-700 hover:border-blue-300 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200",
										)}
									>
										{item.labelKey
											? t(`aspect.presets.${item.labelKey}`)
											: item.value}
									</button>
								))}
							</div>
							{preset !== "free" && source ? (
								<label
									className="mt-4 block text-sm font-semibold"
									htmlFor="crop-size"
								>
									{t("aspect.cropSize")}{" "}
									<span className="text-muted-foreground float-right font-normal">
										{cropSize}%
									</span>
									<input
										id="crop-size"
										type="range"
										min={MIN_CROP_PERCENT}
										max={Math.round(
											Math.min(
												rectForRatio({
													sourceRatio: source.width / source.height,
													targetRatio:
														PRESETS.find((item) => item.value === preset)
															?.ratio ?? 1,
												}).width,
												rectForRatio({
													sourceRatio: source.width / source.height,
													targetRatio:
														PRESETS.find((item) => item.value === preset)
															?.ratio ?? 1,
												}).height,
											),
										)}
										value={cropSize}
										onChange={(event) =>
											adjustLockedCropSize(Number(event.target.value))
										}
										className="mt-2 w-full accent-blue-600"
									/>
								</label>
							) : null}
						</div>

						<div className="rounded-3xl border border-blue-100 bg-white/88 p-4 shadow-sm sm:p-5 dark:border-blue-950 dark:bg-slate-900/82">
							<p className="font-bold">{t("area.title")}</p>
							<div className="mt-3 grid grid-cols-2 gap-3">
								{(["x", "y", "width", "height"] as const).map((field) => (
									<label
										key={field}
										className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300"
									>
										{field === "width"
											? t("area.width")
											: field === "height"
												? t("area.height")
												: field.toUpperCase()}
										<input
											type="number"
											min={field === "x" || field === "y" ? 0 : 1}
											max={
												source && outputPixels
													? field === "x"
														? source.width - 1
														: field === "y"
															? source.height - 1
															: field === "width"
																? source.width - outputPixels.x
																: source.height - outputPixels.y
													: undefined
											}
											disabled={!source || !outputPixels}
											value={outputPixels?.[field] ?? ""}
											onChange={(event) =>
												updatePixelField({
													field,
													value: Number(event.target.value),
												})
											}
											className="bg-background mt-1.5 min-h-11 w-full rounded-xl border px-3 text-base font-semibold normal-case tracking-normal outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
										/>
									</label>
								))}
							</div>
							<p className="text-muted-foreground mt-3 text-xs">
								{t("area.help")}
							</p>
						</div>

						<div className="rounded-3xl border border-blue-100 bg-white/88 p-4 shadow-sm sm:p-5 dark:border-blue-950 dark:bg-slate-900/82">
							<p className="font-bold">{t("output.title")}</p>
							<div className="mt-3 grid grid-cols-2 gap-3">
								<label className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
									{t("output.format")}
									<select
										value={format}
										onChange={(event) => {
											if (isOutputFormat(event.target.value)) {
												setFormat(event.target.value);
											}
										}}
										className="bg-background mt-1.5 min-h-11 w-full rounded-xl border px-3 text-sm font-semibold normal-case tracking-normal"
									>
										<option>MP4</option>
										<option>MOV</option>
										<option>WebM</option>
									</select>
								</label>
								<label className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
									{t("output.quality")}
									<select
										value={quality}
										onChange={(event) => {
											if (isOutputQuality(event.target.value)) {
												setQuality(event.target.value);
											}
										}}
										className="bg-background mt-1.5 min-h-11 w-full rounded-xl border px-3 text-sm font-semibold normal-case tracking-normal"
									>
										<option value="Balanced">{t("output.balanced")}</option>
										<option value="High">{t("output.high")}</option>
										<option value="Smaller file">{t("output.smaller")}</option>
									</select>
								</label>
							</div>
							<div className="mt-3 flex min-h-12 items-center justify-between rounded-xl border bg-slate-50 px-3 dark:bg-slate-950">
								<label htmlFor="keep-audio" className="text-sm font-semibold">
									{t("output.keepAudio")}
								</label>
								<Switch
									id="keep-audio"
									checked={keepAudio}
									onCheckedChange={setKeepAudio}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className="border-t border-blue-100 bg-white/78 p-4 sm:p-6 dark:border-blue-950 dark:bg-slate-950/70">
					<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
						<div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
							<p className="text-sm font-bold">
								{source && outputPixels
									? `${source.width} × ${source.height} → ${outputPixels.width} × ${outputPixels.height}`
									: t("summary.emptyTitle")}
							</p>
							<p className="text-muted-foreground mt-1 text-xs">
								{source && outputPixels
									? t("summary.ready", {
											preset:
												preset === "free"
													? t("aspect.presets.freeform")
													: preset,
											x: outputPixels.x,
											y: outputPixels.y,
											format,
											quality: t(
												quality === "Balanced"
													? "output.balanced"
													: quality === "High"
														? "output.high"
														: "output.smaller",
											),
											audio: t(
												keepAudio ? "summary.audioKept" : "summary.muted",
											),
										})
									: t("summary.emptyDescription")}
							</p>
						</div>
						{phase === "processing" ? (
							<Button size="lg" className="min-h-13 rounded-xl px-7" disabled>
								<Loader2 className="mr-2 size-4 animate-spin" />
								{t("actions.processing", { progress })}
							</Button>
						) : phase === "success" ? (
							<Button
								size="lg"
								className="min-h-13 rounded-xl px-7"
								onClick={downloadResult}
							>
								<Download className="mr-2 size-4" />
								{t("actions.download")}
							</Button>
						) : phase === "failed" ? (
							<Button
								size="lg"
								className="min-h-13 rounded-xl px-7"
								onClick={startCrop}
							>
								<RefreshCcw className="mr-2 size-4" />
								{t("actions.retry")}
							</Button>
						) : (
							<Button
								size="lg"
								className="min-h-13 rounded-xl px-7"
								disabled={phase !== "ready"}
								onClick={startCrop}
							>
								{t("actions.crop")}
							</Button>
						)}
					</div>
					{phase === "success" && resultBlob ? (
						<div
							className="mt-4 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/45 dark:text-emerald-100"
							role="status"
							aria-live="polite"
						>
							<Check className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
							<div className="flex-1">
								<span className="font-semibold">{t("success.title")}</span>
								<span className="ml-2 text-emerald-800 dark:text-emerald-200">
									{t("success.details", {
										size: formatBytes({
											bytes: resultBlob.size,
											locale,
										}),
										width: outputPixels?.width ?? 0,
										height: outputPixels?.height ?? 0,
									})}
								</span>
							</div>
							<Button
								variant="text"
								size="sm"
								onClick={resetToEditor}
								aria-label={t("success.dismiss")}
								className="text-emerald-700 hover:text-emerald-950 dark:text-emerald-200"
							>
								<X className="size-4" />
							</Button>
						</div>
					) : null}
					{phase === "failed" && processingError ? (
						<div
							className="mt-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950 dark:border-rose-900 dark:bg-rose-950/45 dark:text-rose-100"
							role="alert"
							aria-live="assertive"
						>
							<CircleAlert
								className="mt-0.5 size-4 shrink-0"
								aria-hidden="true"
							/>
							<span className="font-semibold">
								{t("errors.failed", { error: processingError })}
							</span>
							<Button
								variant="text"
								size="sm"
								onClick={() => setProcessingError(null)}
								aria-label={t("errors.dismiss")}
								className="ml-auto text-rose-700 hover:text-rose-950 dark:text-rose-200"
							>
								<X className="size-4" />
							</Button>
						</div>
					) : null}
				</div>
			</div>

			<input
				ref={inputRef}
				type="file"
				accept="video/*"
				className="sr-only"
				aria-label={t("drop.inputLabel")}
				onChange={(event: ChangeEvent<HTMLInputElement>) =>
					inspectFile(event.target.files?.[0])
				}
			/>
		</section>
	);
}
